import { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Group, Loader, Select, TextInput } from '@mantine/core';
import { DataTable, type DataTableSortStatus } from 'mantine-datatable';
import { IconSearch, IconX } from '@tabler/icons-react';
import { useDebouncedValue } from '@mantine/hooks';
import dayjs from 'dayjs';
import HttpClient from '../helpers/HttpClient';
import { Match } from '../types/match.type';

const PAGE_SIZE = 25;

interface MatchFilters {
  page: number;
  season: string | null;
  competition: string | null;
  teamType: string | null;
  status: string | null;
  sort: DataTableSortStatus<Match>;
}

function MatchList() {
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState<Match[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const [seasons, setSeasons] = useState<string[]>([]);
  const [competitions, setCompetitions] = useState<string[]>([]);
  const [teamTypes, setTeamTypes] = useState<string[]>([]);

  const [searchText, setSearchText] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchText, 300);
  const prevDebouncedRef = useRef(debouncedSearch);

  const [filters, setFilters] = useState<MatchFilters>({
    page: 1,
    season: null,
    competition: null,
    teamType: null,
    status: null,
    sort: { columnAccessor: 'matchDate', direction: 'desc' },
  });

  const fetchData = useCallback(async (f: MatchFilters, search: string) => {
    setLoading(true);
    try {
      const params: Record<string, string> = {
        skip: String((f.page - 1) * PAGE_SIZE),
        take: String(PAGE_SIZE),
      };
      if (search) params.query = search;
      if (f.season) params.season = f.season;
      if (f.competition) params.competition = f.competition;
      if (f.teamType) params.team = f.teamType;
      if (f.status) params.status = f.status;
      params.sortColumn = f.sort.columnAccessor as string;
      params.sortDir = f.sort.direction;

      const result = await new HttpClient<Match>('/match').getAllPaged(params);
      setRecords(result.data ?? []);
      setTotalRecords(result.total);
    } catch (error) {
      console.error('API call failed:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    new HttpClient<any>('/match/filter-options').getAll().then((data: any) => {
      if (data?.seasons) setSeasons(data.seasons);
      if (data?.competitions) setCompetitions(data.competitions);
      if (data?.teams) setTeamTypes(data.teams);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const textChanged = prevDebouncedRef.current !== debouncedSearch;
    prevDebouncedRef.current = debouncedSearch;

    // When text changes and page isn't 1, reset page. The resulting filters change
    // will re-trigger this effect with page=1 and textChanged=false, doing the fetch.
    if (textChanged && filters.page !== 1) {
      setFilters(prev => ({ ...prev, page: 1 }));
      return;
    }

    fetchData(filters, debouncedSearch);
  }, [debouncedSearch, filters]);

  const setFilter = (update: Partial<Omit<MatchFilters, 'page'>>) =>
    setFilters(prev => ({ ...prev, ...update, page: 1 }));

  return (
    <>
      <h1>Spiele</h1>
      <Group mb="md" align="flex-end" wrap="wrap">
        <TextInput
          label="Mannschaft"
          placeholder="Heim- oder Gastmannschaft…"
          leftSection={<IconSearch size={16} />}
          rightSection={
            searchText ? (
              <IconX size={14} style={{ cursor: 'pointer' }} onClick={() => setSearchText('')} />
            ) : null
          }
          value={searchText}
          onChange={(e) => setSearchText(e.currentTarget.value)}
          w={220}
        />
        <Select
          label="Saison"
          placeholder="Alle"
          data={seasons}
          value={filters.season}
          onChange={(v) => setFilter({ season: v })}
          clearable
          w={150}
        />
        <Select
          label="Bewerb"
          placeholder="Alle"
          data={competitions}
          value={filters.competition}
          onChange={(v) => setFilter({ competition: v })}
          clearable
          w={200}
        />
        <Select
          label="Mannschaftstyp"
          placeholder="Alle"
          data={teamTypes}
          value={filters.teamType}
          onChange={(v) => setFilter({ teamType: v })}
          clearable
          w={130}
        />
        <Select
          label="Status"
          placeholder="Alle"
          data={[
            { value: 'completed', label: 'Abgeschlossen' },
            { value: 'upcoming', label: 'Ausstehend' },
            { value: 'cancelled', label: 'Abgesagt' },
          ]}
          value={filters.status}
          onChange={(v) => setFilter({ status: v })}
          clearable
          w={160}
        />
      </Group>

      {loading && records.length === 0 ? (
        <Loader size="md" />
      ) : (
        <DataTable
          withTableBorder
          borderRadius="sm"
          withColumnBorders
          striped
          highlightOnHover
          fetching={loading}
          records={records}
          columns={[
            {
              accessor: 'matchDate',
              title: 'Datum',
              width: 140,
              sortable: true,
              render: ({ matchDate }) => dayjs(matchDate).format('DD.MM.YYYY HH:mm'),
            },
            {
              accessor: 'season',
              title: 'Saison',
              width: 110,
              sortable: true,
            },
            {
              accessor: 'team',
              title: 'Mannschaft',
              width: 110,
              sortable: true,
              render: ({ team }) => team ? <Badge variant="light" color="indigo" size="sm">{team}</Badge> : '–',
            },
            {
              accessor: 'competition',
              title: 'Bewerb',
              sortable: true,
              render: ({ competition }) => competition || '–',
            },
            {
              accessor: 'homeTeamName',
              title: 'Heim',
              sortable: true,
            },
            {
              accessor: 'awayTeamName',
              title: 'Gast',
              sortable: true,
            },
            {
              accessor: 'homeGoals',
              title: 'Ergebnis',
              width: 90,
              textAlign: 'center',
              render: ({ homeGoals, awayGoals, isCompleted }) =>
                isCompleted && homeGoals != null ? `${homeGoals} : ${awayGoals}` : '–',
            },
            {
              accessor: 'venue',
              title: 'Spielort',
              render: ({ venue }) => venue || '–',
            },
            {
              accessor: 'isCompleted',
              title: 'Status',
              width: 130,
              render: ({ isCompleted, isCancelled }) => {
                if (isCancelled) return <Badge color="red" size="sm">Abgesagt</Badge>;
                if (isCompleted) return <Badge color="green" size="sm">Abgeschlossen</Badge>;
                return <Badge color="gray" size="sm">Ausstehend</Badge>;
              },
            },
          ]}
          totalRecords={totalRecords}
          recordsPerPage={PAGE_SIZE}
          page={filters.page}
          onPageChange={(p) => setFilters(prev => ({ ...prev, page: p }))}
          sortStatus={filters.sort}
          onSortStatusChange={(s) => setFilter({ sort: s })}
        />
      )}
    </>
  );
}

export default MatchList;
